import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import ColumnHeader from "./components/datatable/extra/ColumnHeader";
import AvatarModal from "./components/avatar-modal";

// BL-062 — the learner name now navigates to the dedicated profile page
// (/:companySlug/audience/:id) instead of opening the old drawer modal.
function LearnerNameLink({ id, name }) {
  const navigate = useNavigate();
  const { companySlug } = useParams();
  const go = () => navigate(`/${companySlug}/audience/${id}`);
  return (
    <h3
      className="avatar-name avatar-name--link"
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter") go(); }}
    >
      {name}
    </h3>
  );
}

function IndeterminateCheckbox({ indeterminate, className = "", ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + " cursor-pointer data-checkbox"}
      {...rest}
    />
  );
}

export const audienceColumns = [
  {
    id: "checkbox",
    enableSorting: false,
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => {
      return (
        <div style={{ padding: "0.5rem 0" }}>
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        </div>
      );
    },
  },
  {
    id: ( {row}) => `row_${row.id}`,
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="avatar-info-wrapper">
          <div className="avatar-image-cover">
            <img
              className="avatar-image"
              src={row.getValue("learner").imageUrl}
              alt="learner"
            />
          </div>
        </div>
      )
    }
  },
  {
    accessorKey: "learner",
    enableSorting: false,
    filterFn: "nested",
    header: ({ column }) => <ColumnHeader column={column} title="Learners" />,
    cell: ({ row }) => {
      return (
        <div className="avatar-info-wrapper">
          <div className="avatar-content-cover">
            <LearnerNameLink id={row.original.id} name={row.getValue("learner").name} />
            <a
              className="avatar-mail"
              href={`mailto:${row.getValue("learner").email}`}
            >
              {row.getValue("learner").email}
            </a>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "activityStatus",
    enableSorting: false,
    filterFn: "customArray",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Activity Status" />
    ),
    cell: ({ row }) => {
      const value = row.getValue("activityStatus");
      // BL-061 / Brief 3 — status is "needs attention, not bad customer": no red.
      // Engaged green · Neutral grey · Not engaged amber · At risk deep amber.
      const cls = {
        Engaged: "eng",
        Neutral: "neu",
        "Not engaged": "dis",
        "At Risk": "risk",
        "At risk": "risk",
      }[value] || "neu";

      return (
        <span className={`au-status au-status--${cls}`}>
          <span className="au-status__dot" />
          {value}
        </span>
      );
    },
  },
  {
    accessorKey: "completionRate",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Completion Rate" />
    ),
    cell: ({ row }) => {
      const progressValue = parseInt(row.getValue("completionRate"));

      return (
        <div className="rating-data-table">
          <div className="counter-progress-bar">{progressValue}%</div>
          <div className="rating-progress-bar">
            <div
              className="current-progress-bar"
              style={{ width: `${progressValue}%` }}
            ></div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "totalStudyTime",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Time Spent" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-data-table">{row.getValue("totalStudyTime")}</div>
      );
    },
  },
  {
    accessorKey: "learningStart",
    enableSorting: false,
    header: ({ column }) => (
      <ColumnHeader column={column} title="Join Date" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-data-table">
          {row.getValue("learningStart") ? moment(row.getValue("learningStart")).format("DD-MM-YYYY") : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "uniqueVisits",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Unique Visits" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-data-table">{row.getValue("uniqueVisits")}</div>
      );
    },
  },
  {
    accessorKey: "opens",
    header: ({ column }) => <ColumnHeader column={column} title="Opens" />,
    cell: ({ row }) => {
      return <div className="text-data-table">{row.getValue("opens")}</div>;
    },
  },
  {
    accessorKey: "completions",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Completions" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-data-table">{row.getValue("completions")}</div>
      );
    },
  },
  {
    accessorKey: "satisfactionScore",
    enableSorting: false,
    header: ({ column }) => (
      <ColumnHeader column={column} title="Satisfaction /10" />
    ),
    cell: ({ row }) => {
      // Placeholder until satisfaction capture ships (BL-92); shows "—".
      const s = row.getValue("satisfactionScore");
      return <div className="au-sat">{s === null || s === undefined ? "—" : s}</div>;
    },
  },
  {
    accessorKey: "recentActivity",
    enableSorting: false,
    header: ({ column }) => (
      <ColumnHeader column={column} title="Most Recent activity" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-data-table">
          {row.getValue("recentActivity") ? moment(row.getValue("recentActivity")).format("DD-MM-YYYY, hh:mm a") : "-"}
        </div>
      );
    },
  },
];
